/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useVIPListForm.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Call Center module.
────────────────────────────────────────────────────────────────*/


import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { useSaveWithAction } from '../../../hooks/useSaveWithAction';
import { FormState, VIPListResponse, VIPListMemberResponse, VIPListMember } from '../types';
import { DEFAULT_FORM_STATE } from '../constants';
import { mapResponseToState } from '../utils';

interface UseVIPListFormOptions {
    id?: string;
    duplicateId: string | null;
    isEditMode: boolean;
    navigate: (path: string | number, opts?: { replace?: boolean }) => void;
}

export function useVIPListForm({ id, duplicateId, isEditMode, navigate }: UseVIPListFormOptions) {
    const { t } = useI18n();
    const { showError, showSuccess } = useNotifications();
    const location = useLocation();

    const [formData, setFormData] = useState<FormState>({ ...DEFAULT_FORM_STATE });
    const [originalData, setOriginalData] = useState<FormState>({ ...DEFAULT_FORM_STATE });
    const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const navigateRef = useRef(navigate);
    const showErrorRef = useRef(showError);
    const tRef = useRef(t);
    const lastLoadedIdRef = useRef<string | null>(null);
    const lastLocationKeyRef = useRef<string>('');

    useEffect(() => {
        navigateRef.current = navigate;
        showErrorRef.current = showError;
        tRef.current = t;
    }, [navigate, showError, t]);

    // ── "new" form reset ──────────────────────────────────────────
    useEffect(() => {
        const currentId = id || 'new';
        const savedDataKey = 'vipLists:saveAndNewData';
        const locationKey = location.key || location.pathname + location.search;

        if (!isEditMode && !duplicateId && currentId === 'new') {
            const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
            const lastResetTimestamp = lastLoadedIdRef.current === 'new'
                ? sessionStorage.getItem(savedDataKey + ':lastReset') : null;

            const shouldReset =
                lastLoadedIdRef.current !== currentId ||
                lastLocationKeyRef.current !== locationKey ||
                (lastLoadedIdRef.current === 'new' && currentId === 'new' &&
                    resetTimestamp && resetTimestamp !== lastResetTimestamp);

            if (shouldReset) {
                try {
                    sessionStorage.removeItem(savedDataKey);
                    if (resetTimestamp) {
                        sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
                        sessionStorage.removeItem(savedDataKey + ':reset');
                    }
                } catch { /* ignore */ }

                const defaultState = { ...DEFAULT_FORM_STATE };
                setFormData(defaultState);
                setOriginalData({ ...defaultState });
                lastLoadedIdRef.current = currentId;
                lastLocationKeyRef.current = locationKey;
                setIsLoading(false);
            }
        }
    }, [isEditMode, id, duplicateId, location]);

    // ── Load existing data (edit / duplicate) ─────────────────────
    useEffect(() => {
        const loadId = duplicateId || (isEditMode ? id : null);
        const locationKey = location.key || location.pathname + location.search;

        if (!loadId || loadId === 'new') return;

        const isSwitchingFromNew = lastLoadedIdRef.current === 'new' || lastLoadedIdRef.current === null;
        if (!isSwitchingFromNew && lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) return;

        setIsLoading(true);
        let isActive = true;

        const fetchList = async () => {
            try {
                const listResponse = await fetch(buildApiUrl(`/api/telephony/vip-lists/${loadId}`), { headers: getApiHeaders() });
                if (!listResponse.ok) throw new Error(tRef.current('vipLists.messages.loadError'));

                const listResult = await listResponse.json();
                if (!listResult?.success || !listResult?.data) {
                    throw new Error(listResult?.message || tRef.current('vipLists.messages.loadError'));
                }

                if (!isActive) return;

                let members: VIPListMemberResponse[] = [];
                try {
                    const membersResponse = await fetch(buildApiUrl(`/api/telephony/vip-lists/${loadId}/members`), { headers: getApiHeaders() });
                    if (membersResponse.ok) {
                        const membersResult = await membersResponse.json();
                        if (membersResult?.success && Array.isArray(membersResult.data)) {
                            members = membersResult.data;
                        }
                    }
                } catch { /* members may be empty */ }

                if (!isActive) return;

                const formState = mapResponseToState(listResult.data as VIPListResponse, members);

                if (duplicateId) {
                    setFormData({ ...formState, name: `${formState.name} (Copy)` });
                    setOriginalData({ ...DEFAULT_FORM_STATE });
                } else {
                    setFormData(formState);
                    setOriginalData({ ...formState });
                }

                lastLoadedIdRef.current = loadId;
                lastLocationKeyRef.current = locationKey;
            } catch (error) {
                if (!isActive) return;
                const message = error instanceof Error ? error.message : tRef.current('vipLists.messages.loadError');
                showErrorRef.current(message);
                if (isEditMode) navigateRef.current('/pbx/call-center/vip-lists' as string, { replace: true });
                lastLoadedIdRef.current = null;
            } finally {
                if (isActive) setIsLoading(false);
            }
        };

        void fetchList();
        return () => { isActive = false; };
    }, [duplicateId, id, isEditMode, location]);

    // ── Unmount cleanup ───────────────────────────────────────────
    useEffect(() => () => { lastLoadedIdRef.current = null; }, []);

    // ── Change detection ──────────────────────────────────────────
    const hasChanges = useMemo(() => {
        if (formData.name !== originalData.name) return true;
        if (formData.enabled !== originalData.enabled) return true;
        if (formData.members.length !== originalData.members.length) return true;
        for (let i = 0; i < formData.members.length; i++) {
            const m = formData.members[i];
            const o = originalData.members[i];
            if (!o || m.callerIdNumber !== o.callerIdNumber || m.callerIdName !== o.callerIdName ||
                m.priority !== o.priority || m.notes !== o.notes || m.enabled !== o.enabled) {
                return true;
            }
        }
        return false;
    }, [formData, originalData]);

    // ── Field / member handlers ───────────────────────────────────
    const handleFieldChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleMembersChange = useCallback((members: VIPListMember[]) => {
        setFormData((prev) => ({ ...prev, members }));
    }, []);

    const handleCancel = useCallback(() => {
        setFormData({ ...originalData });
    }, [originalData]);

    // ── Submit / save ─────────────────────────────────────────────
    const handleSubmit = useCallback(async () => {
        const trimmedName = formData.name.trim();
        if (!trimmedName) {
            const error = new Error(t('vipLists.messages.validationError'));
            showError(error.message);
            throw error;
        }

        const url = isEditMode ? `/api/telephony/vip-lists/${id}` : '/api/telephony/vip-lists';
        const method = isEditMode ? 'PUT' : 'POST';

        setIsSaving(true);
        try {
            const payload = { name: trimmedName, description: '', enabled: formData.enabled };
            const response = await fetch(buildApiUrl(url), {
                method,
                headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                let errorMessage = t('vipLists.messages.saveError');
                try { const d = await response.json(); if (d?.message) errorMessage = d.message; } catch { /* noop */ }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            if (!result?.success || !result?.data) throw new Error(result?.message || t('vipLists.messages.saveError'));

            const listId = result.data.uuid || result.data.id;

            // Members: fetch existing (edit mode) then create/update/delete
            let existingMembers: VIPListMemberResponse[] = [];
            if (isEditMode) {
                const existingRes = await fetch(buildApiUrl(`/api/telephony/vip-lists/${listId}/members`), { headers: getApiHeaders() });
                if (existingRes.ok) {
                    const existingResJson = await existingRes.json();
                    if (existingResJson?.success && Array.isArray(existingResJson.data)) existingMembers = existingResJson.data;
                }
            }

            const membersToProcess = formData.members.filter(m => (m.callerIdNumber?.trim() || '').length > 0);
            const existingIds = new Set(existingMembers.map(m => m.id));

            for (const member of membersToProcess) {
                const callerIdNumber = member.callerIdNumber.trim();
                if (!callerIdNumber) continue;

                const memberPayload = {
                    callerIdNumber,
                    callerIdName: (member.callerIdName || '').trim(),
                    priority: member.priority || 1,
                    notes: (member.notes || '').trim(),
                    enabled: member.enabled !== undefined ? member.enabled : true
                };

                if (isEditMode && member.id && existingIds.has(member.id)) {
                    const updateRes = await fetch(buildApiUrl(`/api/telephony/vip-lists/${listId}/members/${member.id}`), {
                        method: 'PUT',
                        headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify(memberPayload)
                    });
                    if (!updateRes.ok) throw new Error(`Failed to update member ${memberPayload.callerIdNumber}`);
                    await updateRes.json();
                } else {
                    const createRes = await fetch(buildApiUrl(`/api/telephony/vip-lists/${listId}/members`), {
                        method: 'POST',
                        headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify(memberPayload)
                    });
                    if (!createRes.ok) throw new Error(`Failed to create member ${memberPayload.callerIdNumber}`);
                    await createRes.json();
                }
            }

            // Delete removed members
            if (isEditMode) {
                const currentIds = new Set(membersToProcess.filter(m => m.id).map(m => m.id!));
                for (const em of existingMembers) {
                    if (!currentIds.has(em.id)) {
                        await fetch(buildApiUrl(`/api/telephony/vip-lists/${listId}/members/${em.id}`), {
                            method: 'DELETE', headers: getApiHeaders()
                        });
                    }
                }
            }

            // Reload state (edit mode)
            if (isEditMode) {
                const reloadRes = await fetch(buildApiUrl(`/api/telephony/vip-lists/${listId}`), { headers: getApiHeaders() });
                if (reloadRes.ok) {
                    const reloadJson = await reloadRes.json();
                    if (reloadJson?.success) {
                        const membersRes = await fetch(buildApiUrl(`/api/telephony/vip-lists/${listId}/members`), { headers: getApiHeaders() });
                        let members: VIPListMemberResponse[] = [];
                        if (membersRes.ok) {
                            const membersJson = await membersRes.json();
                            if (membersJson?.success && Array.isArray(membersJson.data)) members = membersJson.data;
                        }
                        const formState = mapResponseToState(reloadJson.data as VIPListResponse, members);
                        setFormData(formState);
                        setOriginalData({ ...formState });
                    }
                }
            }

            showSuccess(t('vipLists.messages.saveSuccess'));
            return { id: listId, uuid: listId, ...result.data };
        } catch (error) {
            const message = error instanceof Error ? error.message : t('vipLists.messages.saveError');
            showError(message);
            throw error;
        } finally {
            setIsSaving(false);
        }
    }, [formData, isEditMode, id, showError, showSuccess, t]);

    const handleSaveInternal = useCallback(async () => {
        const savedData = await handleSubmit();
        if (!savedData && isEditMode && id) return { id, uuid: id };
        return savedData;
    }, [handleSubmit, isEditMode, id]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/pbx/call-center/vip-lists',
        newPath: '/pbx/call-center/vip-lists/new',
        mode: isEditMode ? 'edit' : 'create',
        getEditPath: (savedData) => `/pbx/call-center/vip-lists/${savedData.uuid || savedData.id}`,
        getFormData: () => ({ formData }),
        storageKey: 'vipLists:saveAndNewData'
    });

    return {
        formData,
        isLoading,
        isSaving,
        hasChanges,
        handleFieldChange,
        handleMembersChange,
        handleCancel,
        handleSaveWithAction,
    };
}
